import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, ContextualMenu } from 'sonata-react-components';

import { HoverTooltip } from '@/components/common/HoverTooltip/HoverTooltip';
import { getGoogleMapsPlaceUrl } from '@/components/doohReport/common/helpers/googleMaps.helper';
import RouteModal from '@/components/doohReport/common/RouteModal/RouteModal';

import type { DoohReportLocation } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './screenMapActions.scss';

type Props = {
  id: string;
  screenLocation: DoohReportLocation | null;
  poiLocation: DoohReportLocation | null;
};

/**
 * Map icon opening a menu with "View Screen on map", "View POI on map" and
 * "View Route" (which in turn opens `RouteModal`). Actions are omitted or
 * disabled when the coordinates they need are missing.
 */
export default function ScreenMapActions({ id, screenLocation, poiLocation }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [isRouteModalOpen, setRouteModalOpen] = useState(false);

  const hasPoi = !!poiLocation;

  return (
    <>
      <HoverTooltip
        content={formatMessage({ defaultMessage: 'Map actions', id: 'doohReport.deliveryDrivers.mapActions.title' })}
      >
        <span>
          <ContextualMenu id={`${id}-menu`} placement="bottom-end">
            <ContextualMenu.Trigger>
              <ButtonUI
                className="screenMapActions__trigger"
                icon="fa-duotone fa-regular fa-map-location-dot"
                id={`${id}-trigger`}
                shape="link"
                size="small"
                type="secondary"
              />
            </ContextualMenu.Trigger>
            <ContextualMenu.Item
              disabled={!screenLocation}
              icon="fa-solid fa-arrow-up-right-from-square"
              id={`${id}-viewScreen`}
              onClick={() =>
                screenLocation && window.open(getGoogleMapsPlaceUrl(screenLocation), '_blank', 'noopener,noreferrer')
              }
              text={formatMessage({
                defaultMessage: 'View Screen on map',
                id: 'doohReport.deliveryDrivers.mapActions.viewScreen'
              })}
            />
            {hasPoi && (
              <ContextualMenu.Item
                icon="fa-solid fa-arrow-up-right-from-square"
                id={`${id}-viewPoi`}
                onClick={() =>
                  poiLocation && window.open(getGoogleMapsPlaceUrl(poiLocation), '_blank', 'noopener,noreferrer')
                }
                text={formatMessage({
                  defaultMessage: 'View POI on map',
                  id: 'doohReport.deliveryDrivers.mapActions.viewPoi'
                })}
              />
            )}
            {hasPoi && (
              <ContextualMenu.Item
                icon="fa-duotone fa-regular fa-map-location-dot"
                id={`${id}-viewRoute`}
                onClick={() => setRouteModalOpen(true)}
                text={formatMessage({
                  defaultMessage: 'View Route',
                  id: 'doohReport.deliveryDrivers.mapActions.viewRoute'
                })}
              />
            )}
          </ContextualMenu>
        </span>
      </HoverTooltip>
      {screenLocation && poiLocation && (
        <RouteModal
          destination={poiLocation}
          destinationIcon="fa-solid fa-location-dot"
          id={`${id}-routeModal`}
          isOpen={isRouteModalOpen}
          onClose={() => setRouteModalOpen(false)}
          origin={screenLocation}
          originIcon="fa-kit fa-screen"
          title={formatMessage({
            defaultMessage: 'Route between the screen and the nearest POI',
            id: 'doohReport.deliveryDrivers.routeModal.title'
          })}
        />
      )}
    </>
  );
}
